import React, { createRef, useState } from "react";
import { View, StyleSheet, ScrollView, SafeAreaView } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { useNavigation } from "@react-navigation/native";
import { Button, Text, theme, typography, ErrorWarningBottomSheet } from "ui-components";
import {
  deriveUnitTitle,
  getPropertyAddressFromPropertyProfile,
} from "../../screens/new/utils/helper";
import { Grid, Container, Row, Column } from "ui-grid";
import moment from "moment";
import automationTestingLocators from "./utils/automation-testing-locators.json";
import { RootState } from "../../redux/reducers";
import { translate } from "../../i18n/translate";
import { Footer } from "../../components/new/footer/footer";
import { BottomNav } from "../../components/layout/bottom-nav";
import { domusoResponsiveStyleHelper } from "./utils/domuso-responsive";
import { DomusoApiService } from "../../api/DomusoApiService";

export const HomepageAutopayManage = (props: any) => {
  const navigation = useNavigation();
  const params = props?.route?.params ? props?.route?.params : props;
  const logger = useSelector((state: RootState) => state.logger);
  let {
    activeAutoPay,
    reservationId,
    prop_name,
    property_unit,
    property_profile,
    autoPayPaymentDeviceInfo,
  } = params;

  const dispatch = useDispatch();

  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const domusoApiService = new DomusoApiService(cognitoUser, dispatch);
  const refPopupWarning = createRef();
  const [processingCancelAutoPayRequest, setProcessingCancelAutoPayRequest] = useState(false);
  const [slideOutTitle, setSlideOutTitle] = useState(
    translate("homepage.autoPayManage.slideOutTitle")
  );
  const [slideOutBody, setSlideOutBody] = useState(
    translate("homepage.autoPayManage.slideOutBody")
  );

  const navigateToHome = () => {
    return navigation.navigate("Home", { ...params });
  };

  const handleDisableAutoPay = () => {
    setProcessingCancelAutoPayRequest(true);
    setSlideOutTitle(translate("homepage.autoPayManage.slideOutTitle"));
    setSlideOutBody(translate("homepage.autoPayManage.slideOutBody"));
    refPopupWarning?.current?.close();
    domusoApiService
      .cancelAutoPay(reservationId)
      .then((resp) => {
        logger.info(`disable autopay`, { resp, reservationId });
        refPopupWarning?.current?.close();
        navigateToHome();
      })
      .catch((e) => {
        logger.error(e);
        setSlideOutTitle(translate("homepage.autoPayManage.slideOutTitleError"));
        setSlideOutBody(translate("homepage.autoPayManage.slideOutBodyError"));
      })
      .finally(() => {
        setProcessingCancelAutoPayRequest(false);
        console.log("disable autopay completed");
      });
  };
  return (
    <SafeAreaView style={styles.container}>
      <View style={{ marginVertical: 20 }}></View>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollableArea}>
        <Grid>
          <Container>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.cardTop}>
                <View style={styles.inputRowInner}>
                  <View style={{ flexDirection: "row", alignItems: "flex-start", flex: 1 }}>
                    <Text style={{ flex: 1 }}>
                      <Text
                        style={styles.propNameStyle}
                        {...automationTestingLocators.autoPayManage.propertyName}>
                        {`${prop_name}`}
                      </Text>
                      <Text
                        style={styles.propUnitStyle}
                        {...automationTestingLocators.autoPayManage.propertyUnit}>
                        {`  (${deriveUnitTitle(property_unit)})`}
                      </Text>
                    </Text>
                  </View>
                  <View>
                    <Text
                      style={styles.propAddressStyle}
                      {...automationTestingLocators.autoPayManage.propertyAddress}>
                      {getPropertyAddressFromPropertyProfile(property_profile)}
                    </Text>
                  </View>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={styles.inputRowInner}>
                  <View style={{ marginVertical: 20 }}></View>
                  <View style={{ flexDirection: "row", flex: 1, alignItems: "center" }}>
                    <Text
                      style={styles.autoPayTitleStyle}
                      {...automationTestingLocators.autoPayManage.autopayTitle}>
                      {translate("homepage.reservationCard.autoPay")}
                    </Text>
                    <Text
                      style={[
                        styles.autoPayStatusStyle,
                        activeAutoPay ? styles.enabledText : styles.disabledText,
                      ]}
                      {...automationTestingLocators.autoPayManage.autopayStatus}>
                      {`${
                        activeAutoPay
                          ? translate("homepage.reservationCard.enabled")
                          : translate("homepage.reservationCard.disabled")
                      }`}
                    </Text>
                  </View>
                  <View>
                    <Text
                      style={styles.scheduleText}
                      {...automationTestingLocators.autoPayManage.autopayScheduleDateText}>
                      {`${translate("homepage.reservationCard.scheduledFor")} ${moment(
                        activeAutoPay?.next_due_date,
                        "MM/DD/YYYY"
                      ).format("MMMM DD, YYYY")} with `}
                      <Text
                        style={{ color: theme.colors.secondary }}
                        {...automationTestingLocators.autoPayManage.autopaySchedulePaymentMethod}>
                        {`${autoPayPaymentDeviceInfo}`}
                      </Text>
                    </Text>
                  </View>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
          </Container>
          <ErrorWarningBottomSheet
            ref={refPopupWarning}
            height={190}
            title={slideOutTitle}
            body={slideOutBody}
            alertType={"warning"}
            leftButtonDisabled={processingCancelAutoPayRequest}
            leftButtonLoading={processingCancelAutoPayRequest}
            leftButtonType={"secondary"}
            leftButtonText={translate("homepage.autoPayManage.slideOutConfirm") as string}
            onPressLeftButton={() => handleDisableAutoPay()}
            rightButtonText={translate("homepage.autoPayManage.slideOutCancel") as string}
            rightButtonDisabled={processingCancelAutoPayRequest}
            onPressRightButton={() => refPopupWarning?.current?.close()}
            {...automationTestingLocators.autoPayManage.confirmAutoPayDisableSlideOut}
          />
        </Grid>
      </ScrollView>

      <BottomNav backgroundColor={theme.colors.white}>
        <View style={{ marginTop: 15 }} />
        <Row>
          <Column span={{ xs: false, sm: 2, md: 3, lg: 3 }} />
          <Column span={{ xs: 12, sm: 8, md: 6, lg: 6 }}>
            <View style={{ paddingVertical: 5 }}>
              <Button
                textStyle={{ fontFamily: "Lato_700Bold" }}
                type={"primary"}
                text={"Disable AutoPay"}
                loading={false}
                disabled={false}
                style={{
                  backgroundColor: theme.colors.red,
                }}
                onPress={() => {
                  refPopupWarning?.current?.open();
                }}
                {...automationTestingLocators.autoPayManage.initiateAutoPayDisableSlideOut}
              />
            </View>
          </Column>
          <Column span={{ xs: false, sm: 2, md: 3, lg: 3 }} />
        </Row>
      </BottomNav>
      <Footer />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    ...domusoResponsiveStyleHelper({
      webPhone: {
        backgroundColor: "#FFFFFF",
      },
      webTablet: {
        backgroundColor: "#FAFAFA",
      },
      webDesktop: {
        backgroundColor: "#FAFAFA",
      },
      iosPhone: {
        flex: 1,
        backgroundColor: "#FFFFFF",
      },
      iosTablet: {
        flex: 1,
        backgroundColor: "#FAFAFA",
      },
      androidPhone: {
        flex: 1,
        backgroundColor: "#FFFFFF",
      },
      androidTablet: {
        flex: 1,
        backgroundColor: "#FAFAFA",
      },
    }),
  },
  scrollableArea: {
    ...domusoResponsiveStyleHelper({
      webPhone: { backgroundColor: "#FFFFFF", paddingBottom: 10 },
      webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      iosPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
      iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      androidPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
      androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
    }),
  },
  scrollView: {
    ...domusoResponsiveStyleHelper({
      webPhone: {
        marginBottom: -5,
      },
      webTablet: {
        marginBottom: -5,
      },
      webDesktop: {
        marginBottom: 0,
      },
      iosPhone: {
        flexGrow: 1,
        marginBottom: 0,
      },
      iosTablet: {
        flexGrow: 1,
        marginBottom: 0,
      },
      androidPhone: {
        flexGrow: 1,
        marginBottom: 0,
      },
      androidTablet: {
        flexGrow: 1,
        marginBottom: 0,
      },
    }),
  },
  cardTop: {
    paddingTop: 8,
    ...domusoResponsiveStyleHelper({
      webPhone: { backgroundColor: "#FAFAFA" },
      webTablet: {
        paddingTop: 16,
        backgroundColor: "#FAFAFA",
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        paddingTop: 16,
        backgroundColor: "#FAFAFA",
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: { backgroundColor: "#FAFAFA" },
      iosTablet: {
        paddingTop: 16,
        backgroundColor: "#FAFAFA",
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: { backgroundColor: "#FAFAFA" },
      androidTablet: {
        paddingTop: 16,
        backgroundColor: "#FAFAFA",
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  inputRow: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  inputRowInner: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      webDesktop: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
    }),
  },
  propNameStyle: {
    ...typography.heading2,
    fontWeight: "500",
    color: theme.colors.secondary,
    marginRight: 4,
    lineHeight: 24,
  },
  propUnitStyle: {
    ...typography.title2,
    fontFamily: "Lato_400Regular",
    fontWeight: "500",
    color: theme.colors.text3,
  },
  propAddressStyle: {
    ...typography.subHeading,
    fontFamily: "Lato_700Bold",
    color: theme.colors.text2,
    flex: 1,
    marginTop: 2,
  },
  autoPayTitleStyle: {
    ...typography.heading2,
    fontSize: 22,
    fontWeight: "500",
    color: theme.colors.secondary,
  },
  autoPayStatusStyle: {
    ...typography.subHeading,
    fontWeight: "500",
    marginLeft: 12,
    marginTop: 5,
    flex: 1,
  },
  enabledText: {
    color: theme.colors.success,
  },
  disabledText: {
    color: theme.colors.darkGrey,
  },
  scheduleText: {
    ...typography.subHeading,
    fontFamily: "Lato_700Bold",
    color: theme.colors.text3,
  },
});
